import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Shield, Target } from 'lucide-react';
import { LossStreakControls } from './LossStreakControls';
import { WinProtectControls } from './WinProtectControls';
import { AntiLossStreakControls } from './AntiLossStreakControls';
import { AutoLossStreakOptimizer } from './AutoLossStreakOptimizer';
import { AutoWinProtectOptimizer } from './AutoWinProtectOptimizer';
import { AutoAntiLossStreakOptimizer } from './AutoAntiLossStreakOptimizer';
import { AnalysisParams } from './types';
import { toast } from 'sonner';

interface RiskManagementTabProps {
  fileId: string | null;
  params: AnalysisParams;
  csvData: any[];
  isGlobal: boolean;
  onUpdateParam: (key: keyof AnalysisParams, value: any) => void;
}

export const RiskManagementTab: React.FC<RiskManagementTabProps> = ({
  fileId,
  params,
  csvData,
  isGlobal,
  onUpdateParam
}) => {
  const hasData = !isGlobal && csvData && csvData.length > 0;

  return (
    <div className="space-y-6">
      {/* Loss Streak Protection Section */}
      <Card className="border-destructive/20 bg-destructive/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Loss Streak Protection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <LossStreakControls
            enabled={params.loss_streak_enabled}
            maxConsecutiveLosses={params.max_consecutive_losses}
            resumeAfterWin={params.resume_after_win}
            onEnabledChange={(enabled) => onUpdateParam('loss_streak_enabled', enabled)}
            onMaxLossesChange={(value) => onUpdateParam('max_consecutive_losses', value)}
            onResumeAfterWinChange={(enabled) => onUpdateParam('resume_after_win', enabled)}
          />
          
          {hasData && (
            <AutoLossStreakOptimizer
              csvData={csvData}
              params={params}
              onApplySettings={(maxLosses) => {
                onUpdateParam('loss_streak_enabled', true);
                onUpdateParam('max_consecutive_losses', maxLosses);
                onUpdateParam('resume_after_win', true);
                toast.success(`Applied optimal loss streak setting: ${maxLosses} consecutive losses`);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Anti Loss Streak Protection Section */}
      <Card className="border-warning/20 bg-warning/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4 text-warning" />
            Anti Loss Streak Protection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <AntiLossStreakControls
            enabled={params.anti_loss_streak_enabled}
            waitForLosses={params.wait_for_losses}
            stopAfterLosses={params.stop_after_losses}
            resetOnWin={params.reset_on_win}
            mode={params.anti_loss_streak_mode}
            onEnabledChange={(enabled) => onUpdateParam('anti_loss_streak_enabled', enabled)}
            onWaitForLossesChange={(value) => onUpdateParam('wait_for_losses', value)}
            onStopAfterLossesChange={(value) => onUpdateParam('stop_after_losses', value)}
            onResetOnWinChange={(enabled) => onUpdateParam('reset_on_win', enabled)}
            onModeChange={(mode) => onUpdateParam('anti_loss_streak_mode', mode)}
          />
          
          {hasData && params.anti_loss_streak_enabled && (
            <AutoAntiLossStreakOptimizer
              csvData={csvData}
              params={params}
              onApplySettings={(waitForLosses, stopAfterLosses) => {
                onUpdateParam('anti_loss_streak_enabled', true);
                onUpdateParam('wait_for_losses', waitForLosses);
                onUpdateParam('stop_after_losses', stopAfterLosses);
                onUpdateParam('reset_on_win', true);
                toast.success(`Applied optimal anti loss streak setting: Wait ${waitForLosses}, Stop ${stopAfterLosses}`);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Win Protection Section */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="h-4 w-4 text-primary" />
            Win Protection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <WinProtectControls
            enabled={params.win_protect_enabled}
            startWins={params.win_protect_start_wins}
            maxWins={params.win_protect_max_wins}
            includeNthWin={params.win_protect_include_nth_win}
            mode={params.win_protect_mode}
            onEnabledChange={(enabled) => onUpdateParam('win_protect_enabled', enabled)}
            onStartWinsChange={(value) => onUpdateParam('win_protect_start_wins', value)}
            onMaxWinsChange={(value) => onUpdateParam('win_protect_max_wins', value)}
            onIncludeNthWinChange={(enabled) => onUpdateParam('win_protect_include_nth_win', enabled)}
            onModeChange={(mode) => onUpdateParam('win_protect_mode', mode)}
          />
          
          {hasData && (
            <AutoWinProtectOptimizer
              csvData={csvData}
              params={params}
              onApplySettings={(startWins, maxWins) => {
                onUpdateParam('win_protect_enabled', true);
                onUpdateParam('win_protect_start_wins', startWins);
                onUpdateParam('win_protect_max_wins', maxWins);
                onUpdateParam('win_protect_include_nth_win', false);
                toast.success(`Applied optimal win protect setting: Start after ${startWins} wins, take ${maxWins} wins`);
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};